import { useMemo } from 'react';

interface AdminSubscriptionOverride {
  status?: 'paid' | 'free' | 'none';
  mode?: 'fallback' | 'override';
  setAt?: number;
  setBy?: string;
}

interface Subscription {
  status?: string;
  planName?: string;
  pendingSince?: number;
  paymentFailed?: boolean;
  failedInvoiceUrl?: string;
  adminSubscriptionOverride?: AdminSubscriptionOverride;
}

interface SubscriptionStatusResult {
  canRecoverCheckout?: boolean;
  isPending: boolean;
  isExpired: boolean;
  displayStatus: string;
  displayPlanName: string;
  showAsNonMember: boolean;
  paymentFailed: boolean;
  failedInvoiceUrl?: string;
}

export const PENDING_TIMEOUT_MS = 2 * 60 * 1000; // Wait before checking an unfinished checkout.

export function useSubscriptionStatus(subscription: Subscription | undefined, currentTime: number): SubscriptionStatusResult {
  return useMemo(() => {
    const adminSubscriptionOverride = subscription?.adminSubscriptionOverride;
    const hasActiveProviderSubscription = subscription?.status === 'active' && subscription.planName && subscription.planName !== 'no_subscription';

    const overrideIsEffective = adminSubscriptionOverride?.mode === 'override';
    if ((overrideIsEffective || !hasActiveProviderSubscription) && adminSubscriptionOverride?.status) {
      const overrideStatus = adminSubscriptionOverride.status;

      if (overrideStatus === 'paid') {
        // Simulate active paid subscription
        return {
          isPending: false,
          isExpired: false,
          displayStatus: 'active',
          displayPlanName: 'Paid (Override)',
          showAsNonMember: false,
          paymentFailed: false,
        };
      } else if (overrideStatus === 'free') {
        // Simulate free tier
        return {
          isPending: false,
          isExpired: false,
          displayStatus: 'active',
          displayPlanName: 'Free Tier (Override)',
          showAsNonMember: false,
          paymentFailed: false,
        };
      }
      // If overrideStatus is 'none', fall through to normal logic
    }

    if (!subscription) {
      return {
        isPending: false,
        isExpired: false,
        displayStatus: 'no_subscription',
        displayPlanName: 'No Subscription',
        showAsNonMember: true,
        paymentFailed: false,
      };
    }

    const { status, planName, paymentFailed, failedInvoiceUrl } = subscription;
    const isPending = status?.startsWith('pending-') || false;

    // Only provider confirmation resolves pending operations; elapsed time is not proof.
    const isExpired = false;

    // Determine display status
    let displayStatus = status || 'no_subscription';
    let displayPlanName = planName || 'No Subscription';
    let showAsNonMember = false;

    if (isPending) {
      // Keep pending visible until provider confirmation or explicit recovery.
      displayStatus = status!;
      displayPlanName = status === 'pending-subscription' ? 'Activating...' :
        status === 'pending-cancel' ? 'Cancelling...' :
          status === 'pending-change-plan' ? 'Changing...' :
            'Processing...';
      showAsNonMember = false;
    } else if (status === 'active') {
      // Active subscription
      displayPlanName = planName === 'no_subscription' ? 'No Subscription' :
        planName === 'free_tier' ? 'Free Tier' :
          planName === 'pay_as_you_go' ? 'Pay As You Go' :
            planName === 'enterprise' ? 'Enterprise' :
              planName?.startsWith('paid_credits_monthly') ? 'Paid (Monthly)' :
                planName?.startsWith('paid_credits_annual') ? 'Paid (Annual)' :
                  planName?.startsWith('paid_credits') ? 'Paid Plan' :
                    'No Subscription';
      showAsNonMember = planName === 'no_subscription';
    } else {
      // Other statuses (canceled, past_due, etc.)
      showAsNonMember = true;
      displayPlanName = 'No Subscription';
    }

    return {
      canRecoverCheckout: status === 'pending-subscription' && (!subscription.pendingSince || currentTime - subscription.pendingSince >= PENDING_TIMEOUT_MS),
      isPending,
      isExpired,
      displayStatus,
      displayPlanName,
      showAsNonMember,
      paymentFailed: paymentFailed || false,
      failedInvoiceUrl,
    };
  }, [subscription, currentTime]);
}
